import { LOGOUT } from "../actions/actionTypes";
import {
  INBOX_LIST_FAILURE,
  INBOX_LIST_REQUEST,
  INBOX_LIST_SUCCESS,
  INBOX_MESSAGE_RECEIVED,
  INBOX_SET_READ,
  INBOX_SUMMARY_FAILURE,
  INBOX_SUMMARY_REQUEST,
  INBOX_SUMMARY_SUCCESS,
  type InboxReduxAction,
} from "../actions/inboxActions";
import type { InboxState } from "../types/inbox";

const REPLY_TYPES = new Set<string>([
  INBOX_SUMMARY_SUCCESS,
  INBOX_SUMMARY_FAILURE,
  INBOX_LIST_SUCCESS,
  INBOX_LIST_FAILURE,
  INBOX_MESSAGE_RECEIVED,
  INBOX_SET_READ,
]);

export const initialInboxState: InboxState = {
  accountUuid: null,
  summary: null,
  summaryLoading: false,
  messages: null,
  listUpdatedAt: null,
  listStatus: "idle",
};

/**
 * Starting work for another account drops everything cached for the last one,
 * so a switch never shows the previous account's badge or list.
 * @param {InboxState} state Current slice.
 * @param {string} accountUuid Account the action is for.
 * @returns {InboxState} The slice for that account.
 */
const forAccount = (state: InboxState, accountUuid: string): InboxState =>
  state.accountUuid === accountUuid
    ? state
    : { ...initialInboxState, accountUuid };

const inboxReducer = (
  state: InboxState = initialInboxState,
  action: InboxReduxAction | { type: string },
): InboxState => {
  const typed = action as InboxReduxAction;
  switch (typed.type) {
    case INBOX_SUMMARY_REQUEST:
      return {
        ...forAccount(state, typed.payload.accountUuid),
        summaryLoading: true,
      };
    case INBOX_LIST_REQUEST:
      return {
        ...forAccount(state, typed.payload.accountUuid),
        listStatus: "loading",
      };
    default:
      break;
  }

  if (action.type === LOGOUT) return initialInboxState;

  // Every other inbox action is a reply. A reply for an account that is no
  // longer shown is dropped, and actions from elsewhere (redux-persist's
  // REHYDRATE carries a payload too) never get past here.
  if (
    !REPLY_TYPES.has(typed.type) ||
    typed.payload?.accountUuid !== state.accountUuid
  ) {
    return state;
  }

  switch (typed.type) {
    case INBOX_SUMMARY_SUCCESS:
      return {
        ...state,
        summary: typed.payload.summary,
        summaryLoading: false,
      };
    case INBOX_SUMMARY_FAILURE:
      return { ...state, summaryLoading: false };
    case INBOX_LIST_SUCCESS:
      return {
        ...state,
        messages: typed.payload.messages,
        listUpdatedAt: typed.payload.latestUpdatedAt,
        listStatus: "ready",
      };
    case INBOX_LIST_FAILURE:
      return { ...state, listStatus: "error" };
    case INBOX_MESSAGE_RECEIVED: {
      const { message } = typed.payload;
      if (!state.messages) return state;
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === message.id ? message : m,
        ),
      };
    }
    case INBOX_SET_READ: {
      const { id, read, previousRead } = typed.payload;
      const delta = read === previousRead ? 0 : read ? -1 : 1;
      return {
        ...state,
        messages: state.messages
          ? state.messages.map((m) => (m.id === id ? { ...m, read } : m))
          : state.messages,
        summary: state.summary
          ? {
              ...state.summary,
              unreadCount: Math.max(0, state.summary.unreadCount + delta),
            }
          : state.summary,
      };
    }
    default:
      return state;
  }
};

export default inboxReducer;
